import React, { FC, useEffect, useState } from "react";

import { FormDateRangeInput } from "stentor-models";

import { FormFieldProps } from "../FormDisplay";

/**
 * The value this field reports through onFieldChange, and the shape it accepts
 * back as its initial value. Both halves are "YYYY-MM-DD" - what an
 * <input type="date"> reads and writes.
 */
export interface FormDateRangeValue {
    from: string;
    to: string;
}

export interface FormDateRangeProps extends FormFieldProps {
    readonly field: FormDateRangeInput;
    /**
     * Initial value, so the field can be pre-populated or resumed. Dates may
     * arrive as strings (stored form config is JSON) or as real Date objects.
     */
    readonly value?: { from?: string | Date; to?: string | Date };
    /**
     * When this is the last field of the step the keyboard shows "done" rather than "next".
     */
    readonly isLastField?: boolean;
}

export const FROM_LABEL = "From";
export const TO_LABEL = "To";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

/**
 * Coerce whatever the config or the model hands us into the "YYYY-MM-DD" an
 * <input type="date"> accepts. Anything we cannot parse becomes an empty field
 * rather than a date we invented.
 */
function toInputDate(value?: string | Date): string {
    if (!value) {
        return "";
    }

    if (typeof value === "string" && ISO_DATE.test(value)) {
        // Already in the right form - including the leading date of a full ISO timestamp.
        return value.slice(0, 10);
    }

    const date = value instanceof Date ? value : new Date(value);

    // A date-only string parses as UTC midnight, so read the UTC parts back out.
    // Going through the local parts would shift the day east of Greenwich.
    return isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

export const FormFieldDateRange: FC<FormDateRangeProps> = (props) => {
    const { field, onFieldChange, value, errorMessage, required, isLastField } = props;

    // preselecteDates is the model's spelling - it is the field's own default,
    // used only when the step has no value for us already.
    const initialFrom = toInputDate(value?.from || field.preselecteDates?.from);
    const initialTo = toInputDate(value?.to || field.preselecteDates?.to);

    const [range, setRange] = useState<FormDateRangeValue>({ from: initialFrom, to: initialTo });

    useEffect(() => {
        setRange({ from: initialFrom, to: initialTo });
    }, [initialFrom, initialTo]);

    const change = (next: FormDateRangeValue) => {
        setRange(next);
        // An untouched range reports as undefined rather than a pair of empty
        // strings: an object with keys reads as "filled" to the mandatory checks.
        onFieldChange(field.name, next.from || next.to ? next : undefined);
    };

    const onFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        change({ ...range, from: e.target.value });
    };

    const onToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        change({ ...range, to: e.target.value });
    };

    const fromId = `${field.name}-from`;
    const toId = `${field.name}-to`;

    // No autoComplete on either input: there is no standard token for an arbitrary
    // from/to pair, and a wrong autocomplete is worse than none - the same rule
    // formatToInputProps follows. type="date" is what keeps mobile off the text
    // keyboard, so there is no iOS auto-zoom to guard against either.
    return (
        <div style={field.style}>
            {field.title && <h2>{field.title}</h2>}
            <label htmlFor={fromId}>{FROM_LABEL}</label>
            <input
                id={fromId}
                name={fromId}
                type="date"
                value={range.from}
                onChange={onFromChange}
                max={range.to || undefined}
                required={required}
                enterKeyHint="next"
            />
            <label htmlFor={toId}>{TO_LABEL}</label>
            <input
                id={toId}
                name={toId}
                type="date"
                value={range.to}
                onChange={onToChange}
                min={range.from || undefined}
                required={required}
                enterKeyHint={isLastField ? "done" : "next"}
            />
            {errorMessage && <p>{errorMessage}</p>}
        </div>
    );
};

export default FormFieldDateRange;
